import {Controller, useFieldArray, useFormContext} from "react-hook-form";
import {usePricingCurrencies} from "@/features/currencies";
import {
  OfferingFormCurrencyRow,
  OfferingFormPriceType,
  toBaseScalarPricing,
  toUniformCurrencyRows,
  transitionOfferingFormCurrencyRows,
} from "@/features/pricing";
import {IS_PWYW_ENABLED} from "../../../../../services/config";
import {FormField} from "../../../../../design-system/form-field";
import {CardRadioGroup, CardRadioOption} from "../../../../shared/card-radio-group";
import {UNSET_PWYW_AMOUNT} from "../pwyw-currencies";
import {PricingModelCardsFormFieldProps, PricingModelFormFieldsType} from "./types";

export const PWYW_CARD_TITLE = "Pay what you want (PWYW)";
export const PWYW_CARD_DESCRIPTION = "Allow customers to name a price";

/**
 * The three Pricing Models a coach chooses between, and what choosing one does to the
 * currency rows they have already filled in.
 *
 * Shared by every offering type: the models, their copy, the payout-method guidance and
 * the transition rules are the same everywhere. What differs is only whether a switch
 * costs the coach anything, which is what `confirmChange` is for — and, for live events
 * and booking services, where the base currency lives.
 */
export function PricingModelCardsFormField({
  confirmChange,
  priceTypeField = "priceType",
  baseCurrencyInScalars = false,
}: PricingModelCardsFormFieldProps) {
  const pricingCurrencies = usePricingCurrencies();
  const {control, clearErrors, getValues, setValue} =
    useFormContext<PricingModelFormFieldsType>();

  // Both spellings are fields of the form type; react-hook-form wants one literal path.
  const priceTypeName = priceTypeField as "priceType";

  // `replace`, not `setValue`: swapping a shorter array in through `setValue` leaves the
  // rows past the new end still registered, so a three-currency offering would keep two
  // ghost rows on its way to Free.
  const {replace: replaceCurrencies} = useFieldArray({control, name: "currencies"});

  const {isBankConnected, errorMessageString} = pricingCurrencies.connectedBankStatus;
  const payoutSetupNeeded = isBankConnected ? null : errorMessageString;

  const options: CardRadioOption[] = [
    {
      value: "free",
      title: "Free",
      description: "Give customers free access",
    },
    {
      value: "paid",
      title: "Single fee",
      description: "Charge a one-time fee",
      disabled: !isBankConnected,
      tooltipTitle: payoutSetupNeeded,
    },
    ...(IS_PWYW_ENABLED
      ? [
          {
            value: "pwyw",
            title: PWYW_CARD_TITLE,
            description: PWYW_CARD_DESCRIPTION,
            disabled: !isBankConnected,
            tooltipTitle: payoutSetupNeeded,
          },
        ]
      : []),
  ];

  /**
   * Carries the coach's currency rows across a change of Pricing Model.
   *
   * Single Fee and Pay What You Want share the same underlying price field, so the
   * amounts survive that switch instead of being retyped. Free ↔ Single Fee is left
   * exactly as it was before this feature existed — the slice says so by returning
   * nothing at all for those.
   */
  function carryCurrencyRowsAcross(
    from: OfferingFormPriceType,
    to: OfferingFormPriceType
  ) {
    clearErrors("currencies");

    if (baseCurrencyInScalars && to === "free") {
      // The base price is not part of a currency row, so dropping the rows does not
      // clear it. These two types have always zeroed it on the way to Free.
      clearErrors("callPrice");
      setValue("callPrice", 0);
    }

    const rows = transitionOfferingFormCurrencyRows(from, to, currentCurrencyRows(to));

    if (!rows) {
      return;
    }

    const filledRows = rows.map((row) => ({
      // A row arriving from Free has no currency yet; row one is the base currency.
      callPriceCurrency: row.callPriceCurrency || pricingCurrencies.formCurrencies[0],
      callPrice: row.callPrice ?? UNSET_PWYW_AMOUNT,
      minCallPrice: row.minCallPrice ?? UNSET_PWYW_AMOUNT,
    }));

    if (baseCurrencyInScalars && from === "pwyw") {
      splitBaseCurrencyOut(filledRows, to);
      return;
    }

    replaceCurrencies(filledRows);
  }

  /**
   * The rows the transition should judge, as one uniform list.
   *
   * Under Pay What You Want every offering type already holds all its rows in
   * `currencies`. Under the other two models the base-scalar types keep row one outside
   * the array, so entering Pay What You Want has to fold it back in — otherwise the base
   * currency arrives in the rows with no Minimum Price field to fill in.
   */
  function currentCurrencyRows(to: OfferingFormPriceType): OfferingFormCurrencyRow[] {
    const rows = getValues("currencies") ?? [];

    if (!baseCurrencyInScalars || to !== "pwyw") {
      return rows;
    }

    return toUniformCurrencyRows({
      callPriceCurrency:
        getValues("callPriceCurrency") || pricingCurrencies.formCurrencies[0],
      callPrice: getValues("callPrice") ?? 0,
      currencies: rows,
    });
  }

  /**
   * Puts row one back in the scalars on the way out of Pay What You Want, leaving the
   * array holding every currency except the base one.
   */
  function splitBaseCurrencyOut(
    rows: OfferingFormCurrencyRow[],
    to: OfferingFormPriceType
  ) {
    const pricing = toBaseScalarPricing(rows);

    // Going to Free leaves the base currency alone. The rows it splits are the blank one
    // the transition leaves behind, so writing them to the scalars would replace the
    // coach's own currency with whichever the picker happened to offer first.
    if (to !== "free") {
      setValue("callPriceCurrency", pricing.callPriceCurrency);
      setValue("callPrice", pricing.callPrice);
    }
    replaceCurrencies(pricing.currencies);
  }

  return (
    <FormField
      id="priceType"
      renderInput={(id) => (
        <Controller
          control={control}
          name={priceTypeName}
          render={({field: {onChange, value, name, onBlur, ref}}) => {
            function applyPricingModel(to: OfferingFormPriceType) {
              carryCurrencyRowsAcross(value, to);
              onChange(to);
            }

            async function handleSelect(selected: string) {
              const to = selected as OfferingFormPriceType;

              if (confirmChange && !(await confirmChange(to))) {
                return;
              }

              applyPricingModel(to);
            }

            return (
              <CardRadioGroup
                id={id}
                name={name}
                value={value}
                onChange={handleSelect}
                onBlur={onBlur}
                ref={ref}
                options={options}
                twoPerRow
              />
            );
          }}
        />
      )}
    />
  );
}
